import axios, { AxiosError } from 'axios';
import { CareHome, SearchRequest } from '../types';

const API_BASE_URL = '';

class ApiService {
    private axiosInstance;

    constructor() {
        this.axiosInstance = axios.create({
            baseURL: API_BASE_URL,
            timeout: 10000,
        });
    }

    async searchCareHomes(searchRequest: SearchRequest): Promise<{
        success: boolean;
        data: CareHome[];
        count: number;
        searchCenter: { latitude: number; longitude: number; postcode: string };
    }> {
        try {
            const response = await this.axiosInstance.post('/api/search', searchRequest);
            return response.data;
        } catch (error) {
            console.error('Search API error:', error);
            const axiosErr = error as AxiosError<{ message?: string; error?: string }>;
            const serverMessage = axiosErr.response?.data?.message || axiosErr.response?.data?.error;
            throw new Error(serverMessage || 'Failed to search care homes');
        }
    }

    async searchByCareHomeId(id: string): Promise<{
        success: boolean;
        data: CareHome[];
        count: number;
        searchCenter: { latitude: number; longitude: number; postcode: string };
        radiusMiles: number;
    }> {
        try {
            const response = await this.axiosInstance.post('/api/search', { id });
            return response.data;
        } catch (error) {
            console.error('Search by ID API error:', error);
            throw new Error('Failed to search care homes by ID');
        }
    }

    async getEventList(): Promise<{ events: string[] }> {
        try {
            const response = await this.axiosInstance.get('/api/eventlist');
            return response.data;
        } catch (error) {
            console.error('Event list API error:', error);
            return { events: [] };
        }
    }

    async healthCheck(): Promise<{ status: string; timestamp: string; service: string }> {
        try {
            const response = await this.axiosInstance.get('/api/health');
            return response.data;
        } catch (error) {
            console.error('Health check API error:', error);
            throw new Error('Health check failed');
        }
    }

    /**
     * Track user action (click on Details, Contact, or Manage links)
     * Fire-and-forget - does not block user navigation
     * @param params Action tracking parameters
     */
    trackUserAction(params: {
        regulator_id: string;
        user_action: 'details' | 'contact' | 'manage';
        user_id: string;
        starting_post_code: string;
        search_position: number;
    }): void {
        // Fire-and-forget - don't await, don't block user interaction
        this.axiosInstance.post('/api/action', params)
            .then(() => {
                console.log(`🖱️ Action tracked: ${params.user_action} for ${params.regulator_id}`);
            })
            .catch((error) => {
                // Log error but don't disrupt user experience
                console.error('Failed to track user action:', error);
            });
    }

    /**
     * Fetch address + lat/lng for an event (used by Add to calendar) and log the
     * add_to_calendar user action server-side. (update 2026_04.22.4)
     */
    async fetchEventCalendarInfo(params: {
        regulator_id: string;
        event_id: string;
        user_id: string;
        browser_session: string;
        starting_post_code?: string;
        search_position?: number;
    }): Promise<{
        success: boolean;
        carehome_name: string;
        address_1: string;
        address_2: string;
        town: string;
        county: string;
        post_code: string;
        latitude: number;
        longitude: number;
    }> {
        try {
            const response = await this.axiosInstance.post('/api/event/calendar', params);
            return response.data;
        } catch (error) {
            console.error('Event calendar API error:', error);
            const axiosErr = error as AxiosError<{ message?: string; error?: string }>;
            const serverMessage = axiosErr.response?.data?.message || axiosErr.response?.data?.error;
            throw new Error(serverMessage || 'Failed to retrieve event calendar info');
        }
    }
}

export default new ApiService(); 